import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { api, authService } from '../services/apiService'; // Import the configured API service

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const validateToken = useCallback(async (token) => {
    try {
      // Use the configured API service instead of raw axios
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await authService.getCurrentAdmin(); // Calls /api/auth/me
      
      if (!response) {
        throw new Error('No admin data received');
      }

      setAdmin({
        ...response,
      });
      
      return true;
    } catch (error) {
      console.error('Token validation error:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        delete api.defaults.headers.common['Authorization'];
      }
      return false;
    }
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (token) {
          const isValid = await validateToken(token);
          if (!isValid) {
            toast.warn('Your session has expired. Please log in again.');
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [validateToken]);

  const login = async (email, password) => {
    try {
      setLoading(true);
      
      // Use the authService instead of raw axios
      const response = await authService.login(email.trim(), password.trim());
      
      if (!response) {
        throw new Error('No response data from server');
      }

      const { token, ...userData } = response;
      
      if (!token) {
        throw new Error('No authentication token received');
      }

      localStorage.setItem('adminToken', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const isValid = await validateToken(token);
      
      if (!isValid) {
        throw new Error('Token validation failed');
      }

      toast.success(`Welcome back, ${userData.name || 'Admin'}!`);
      
      return userData;
    } catch (error) {
      console.error('Login error details:', {
        message: error.message,
        response: error.response?.data,
        config: error.config
      });
      
      let errorMessage = 'Login failed';
      
      if (error.response) {
        errorMessage = error.response.data?.error ||
                      error.response.data?.message ||
                      error.response.statusText ||
                      'Login failed';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    delete api.defaults.headers.common['Authorization'];
    setAdmin(null);
    toast.info('Logged out successfully');
  };

  const value = {
    admin,
    loading,
    login,
    logout,
    isAuthenticated: !!admin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};